/**
 * This component is meant to be used for inline styled links.
 * It is primarily aesthetic, although it also manages internal vs.
 * external linking.
 */
import * as React from "react";
import styled, { css } from "styled-components";
import { BaseLink, LinkProps as UnstyledProps } from "./BaseLink";
import { Icon, IconSvg } from "./Icon";
import { mediaQueries, style } from "./style";
import { focusOutline, linkFocusOutline } from "./utils/focusOutlineStyle";

const LinkElem = styled(BaseLink)<LinkProps & { $bold: boolean }>`
  align-items: center;
  display: inline-flex;
  position: relative;
  text-decoration: ${({ type }) => (type !== "breadcrumb" ? "underline" : "none")};
  font-weight: ${({ $bold, type }) => ($bold || type === "external" ? style.vars.fontWeight.bold : "initial")};

  ${({ enableLineBreak }) =>
    enableLineBreak
      ? css`
          display: inline;

          ${linkFocusOutline()}
        `
      : css`
          &&:active,
          &&:focus {
            ${focusOutline()}
            text-decoration: none;
            border-bottom-color: transparent;
          }

          &&:hover {
            text-decoration: none;
            border-bottom-color: transparent;
          }
        `}

  ${({ variation }) => {
    switch (variation) {
      case "secondary":
        return css`
          color: ${style.colors.grey.dark};
          border-color: ${style.colors.grey.dark};

          &:hover {
            color: ${style.colors.grey.primary};
          }

          &&:active,
          &&:focus {
            color: ${style.colors.grey.darkest};
          }
        `;
      case "alert":
        return css`
          color: ${style.colors.grey.darkest};

          &:hover {
            color: ${style.colors.grey.primary};
          }

          &&:active,
          &&:focus {
            color: ${style.colors.grey.dark};
          }
        `;
      default:
        return css`
          color: ${style.colors.blue.dark};

          &:hover {
            color: ${style.colors.blue.dark};
          }

          &&:active,
          &&:focus {
            color: ${style.colors.blue.darkest};
          }
        `;
    }
  }}
`;

type BadgeProps = {
  badgeBorder?: boolean;
};

const BadgeIconWrapper = styled.span<BadgeProps>`
  display: inline-flex;
  margin-right: 1em;
  vertical-align: middle;

  ${IconSvg} {
    /*
      Setting the margin, height, and width based on the font size
      will allow the badge to scale accordingly.
    */
    height: 2em;
    width: 2em;

    ${(props) =>
      props.badgeBorder &&
      css`
        border: ${style.vars.borderWidth.base} solid;
        border-radius: ${style.vars.borderRadius.base};
        padding: ${style.vars.space["1"]};
      `};
  }
`;

const LeftIconWrapper = styled.span`
  display: inline-flex;
  margin-right: ${style.vars.space["1"]};
`;

const LargerTouchTarget = styled.span`
  position: absolute;
  z-index: 0;
  top: -${style.vars.space["2"]};
  bottom: -${style.vars.space["2"]};
  left: 0;
  right: 0;

  ${mediaQueries.mediumMin} {
    display: none;
  }
`;

const ExternalWrapper = styled.span`
  display: relative;
`;

export type LinkProps = UnstyledProps & {
  to: string;
  type?: "breadcrumb" | "badge" | "external" | "default";
  children: React.ReactNode;
  badgeBorder?: boolean;
  icon?: string;
  variation?: "default" | "alert" | "secondary";
  bold?: boolean;
  dark?: boolean;
};

export const Link: React.FC<LinkProps> = ({
  to,
  type = "default",
  children,
  badgeBorder,
  icon,
  variation = "default",
  bold = false,
  enableLineBreak = false,
  target,
  ...delegated
}) => {
  let linkChildren: React.ReactNode[];

  if (type === "breadcrumb") {
    linkChildren = [
      <LeftIconWrapper key="breadcrumb">
        <Icon icon="arrow-left" />
      </LeftIconWrapper>,
      <LargerTouchTarget key="touch-target" />,
      children,
    ];
  } else if (type === "badge" && icon) {
    linkChildren = [
      <BadgeIconWrapper badgeBorder={badgeBorder} key="badge">
        <Icon icon={icon} />
      </BadgeIconWrapper>,
      <LargerTouchTarget key="touch-target" />,
      children,
    ];
  } else if (type === "external") {
    linkChildren = [
      <LargerTouchTarget key="touch-target" />,
      <ExternalWrapper key="external">
        {children}
        <Icon icon={icon ? icon : "external-link"} />
      </ExternalWrapper>,
    ];

    target ??= "_blank";
  } else {
    linkChildren = [children];
  }

  return (
    <LinkElem to={to} type={type} variation={variation} $bold={bold} enableLineBreak={enableLineBreak} target={target} {...delegated}>
      {linkChildren}
    </LinkElem>
  );
};
